// src/components/editor/CollaboratorsDialog.jsx
import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Typography, Chip, Box, Divider,
  Avatar, IconButton, CircularProgress,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import { API_BASE } from '../../utils/api';

const ROLE_CONFIG = {
  edit: {
    label: 'EDIT',
    description: 'Can read and write',
    chipText: '✏️ Can work on the document, save progress, and export',
    color: '#5A52A3',
    bg: '#ede9fe',
    border: '#c4b5fd',
  },
  view_only: {
    label: 'VIEW ONLY',
    description: 'Can view & provide feedback',
    chipText: '👁️ Can view the document and leave feedback comments',
    color: '#6b7280',
    bg: '#f9fafb',
    border: '#d1d5db',
  },
};

const STATUS_COLORS = {
  pending: 'warning',
  accepted: 'success',
  rejected: 'error',
};

const MAX_MESSAGE = 500;

function isExpired(d) { return d ? new Date(d) < new Date() : false; }
function isOverdue(d) { return d ? new Date(d) < new Date() : false; }

const CollaboratorsDialog = ({
  open,
  onClose,
  onAddCollaborator,
  existingCollaborators,
  outgoingInvitations = [],
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [expirationDate, setExpirationDate] = useState('');
  const [role, setRole] = useState('edit');
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    if (!open) {
      setQuery('');
      setResults([]);
      setLoading(false);
      setMessage('');
      setDueDate('');
      setExpirationDate('');
      setRole('edit');
      setSelectedUser(null);
      return;
    }

    if (query.trim().length < 2) {
      setResults([]);
      return;
    }

    let cancelled = false;
    const runSearch = async () => {
      try {
        setLoading(true);
        const accessToken = localStorage.getItem('rv_access_token');
        const res = await fetch(
          `${API_BASE}/api/users/search?q=${encodeURIComponent(query.trim())}`,
          {
            credentials: 'omit',
            headers: {
              'Accept': 'application/json',
              ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
            },
          }
        );
        if (!res.ok) { setResults([]); return; }
        const data = await res.json();
        if (!cancelled) setResults(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    const timeoutId = setTimeout(runSearch, 250);
    return () => { cancelled = true; clearTimeout(timeoutId); };
  }, [open, query]);

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setQuery('');
    setResults([]);
  };

  const handleSend = () => {
    if (!selectedUser || !onAddCollaborator) return;
    onAddCollaborator(selectedUser, {
      role,
      message: message.trim() || undefined,
      dueDate: dueDate || undefined,
      expirationDate: expirationDate || undefined,
    });
    setSelectedUser(null);
    setQuery('');
    setResults([]);
  };

  const existingIds = new Set(
    (existingCollaborators || []).map((c) => c?._id || c?.id).filter(Boolean)
  );
  const pendingInviteIds = new Set(
    (outgoingInvitations || []).filter(i => i.status === 'pending').map(i => i.toUserId).filter(Boolean)
  );
  const filteredResults = results.filter(
    (u) => !existingIds.has(u.id) && !pendingInviteIds.has(u.id) && u.username
  );

  const draftInvitations = (outgoingInvitations || []).filter(i => i.type === 'draft');
  const cfg = ROLE_CONFIG[role];
  const actionLabel = role === 'edit' ? 'Invite as Editor' : 'Invite as Viewer';

  const getAvatarLetter = (name) => (name || 'U')[0].toUpperCase();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" fontWeight={600}>Add Collaborators</Typography>
        <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>

        {/* Search bar */}
        <Box sx={{ position: 'relative', mb: 2 }}>
          {selectedUser ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, bgcolor: '#ede9fe', borderRadius: 1, border: '1px solid #c4b5fd' }}>
              <Avatar sx={{ width: 28, height: 28, fontSize: '0.8rem', bgcolor: '#5A52A3' }}>
                {getAvatarLetter(selectedUser.username)}
              </Avatar>
              <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>{selectedUser.username}</Typography>
              <Chip label="Change" size="small" onClick={() => setSelectedUser(null)} sx={{ cursor: 'pointer', fontSize: '0.7rem' }} />
            </Box>
          ) : (
            <>
              <TextField
                fullWidth
                size="small"
                placeholder="Search users by username..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                autoFocus
                InputProps={{ endAdornment: loading ? <CircularProgress size={16} /> : null }}
              />
              {(filteredResults.length > 0 || (!loading && query.trim().length >= 2)) && (
                <Box sx={{
                  position: 'absolute', top: '100%', left: 0, right: 0, mt: 0.5,
                  bgcolor: 'white', border: '1px solid #e0e0e0', borderRadius: 1,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.12)', zIndex: 20,
                  maxHeight: 200, overflow: 'auto',
                }}>
                  {filteredResults.length > 0 ? filteredResults.map((user) => (
                    <Box
                      key={user.id}
                      onClick={() => handleSelectUser(user)}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 1,
                        p: 1, cursor: 'pointer',
                        '&:hover': { bgcolor: '#f5f5f5' },
                      }}
                    >
                      <Avatar sx={{ width: 28, height: 28, fontSize: '0.875rem', bgcolor: '#5A52A3' }}>
                        {getAvatarLetter(user.username)}
                      </Avatar>
                      <Typography variant="body2">{user.username}</Typography>
                    </Box>
                  )) : (
                    <Box sx={{ p: 1.5 }}>
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>No users found</Typography>
                    </Box>
                  )}
                </Box>
              )}
            </>
          )}
        </Box>

        {/* Role picker - visual cards */}
        <Typography variant="body2" sx={{ color: '#374151', fontWeight: 600, mb: 1 }}>
          Assign role for this group:
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
          {Object.entries(ROLE_CONFIG).map(([roleKey, rcfg]) => {
            const isSelected = role === roleKey;
            return (
              <Box
                key={roleKey}
                onClick={() => setRole(roleKey)}
                sx={{
                  flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
                  gap: 0.75, py: 2, px: 1, borderRadius: '10px', cursor: 'pointer',
                  border: `2px solid ${isSelected ? rcfg.border : '#e5e7eb'}`,
                  backgroundColor: isSelected ? rcfg.bg : '#f9fafb',
                  transition: 'all 0.15s ease',
                  '&:hover': { borderColor: rcfg.border, backgroundColor: rcfg.bg },
                }}
              >
                {roleKey === 'edit'
                  ? <EditIcon sx={{ color: isSelected ? rcfg.color : '#9ca3af', fontSize: 26 }} />
                  : <VisibilityIcon sx={{ color: isSelected ? rcfg.color : '#9ca3af', fontSize: 26 }} />}
                <Typography variant="body2" fontWeight={700} sx={{
                  color: isSelected ? rcfg.color : '#6b7280',
                  textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.75rem',
                }}>
                  {rcfg.label}
                </Typography>
                <Typography variant="caption" sx={{
                  color: isSelected ? rcfg.color : '#9ca3af',
                  fontSize: '0.68rem', textAlign: 'center', textTransform: 'none',
                }}>
                  {rcfg.description}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* Descriptive chip */}
        <Chip
          label={cfg.chipText}
          variant="outlined"
          sx={{
            borderColor: cfg.border,
            color: cfg.color,
            backgroundColor: cfg.bg,
            fontSize: '0.75rem', width: '100%', justifyContent: 'flex-start', mb: 2,
          }}
        />

        {/* Due Date + Expiration Date */}
        <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
          <TextField
            label="Due Date"
            type="date"
            size="small"
            fullWidth
            value={dueDate}
            onChange={e => setDueDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Expiration Date"
            type="date"
            size="small"
            fullWidth
            value={expirationDate}
            onChange={e => setExpirationDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Box>

        {/* Assignment Detail */}
        <Typography variant="body2" sx={{ color: '#374151', fontWeight: 600, mb: 0.75 }}>
          Assignment Detail{' '}
          <Typography component="span" variant="caption" sx={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</Typography>
        </Typography>
        <TextField
          fullWidth
          multiline
          minRows={3}
          value={message}
          onChange={e => setMessage(e.target.value.slice(0, MAX_MESSAGE))}
          sx={{ mb: 0.25 }}
        />
        <Typography variant="caption" sx={{
          color: message.length >= MAX_MESSAGE ? 'error.main' : '#9ca3af',
          display: 'block', textAlign: 'right', mb: 1,
        }}>
          {message.length} / {MAX_MESSAGE}
        </Typography>

        {/* Sent invitations */}
        {draftInvitations.length > 0 && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Typography variant="body2" sx={{ color: '#374151', fontWeight: 600, mb: 1 }}>
              Sent invitations
            </Typography>
            {draftInvitations.map((inv) => {
              const expired = inv.status === 'pending' && isExpired(inv.expirationDate);
              const overdue = inv.status === 'pending' && isOverdue(inv.dueDate);
              return (
                <Box
                  key={inv.id}
                  sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    py: 0.75, px: 1, mb: 0.5, borderRadius: '6px', backgroundColor: '#f9fafb',
                  }}
                >
                  <Typography variant="body2" noWrap>{inv.toUsername || 'Unknown'}</Typography>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    {expired && (
                      <Chip size="small" label="Expired" sx={{ fontSize: '0.65rem', height: 18, bgcolor: '#fee2e2', color: '#ef4444', fontWeight: 700 }} />
                    )}
                    {!expired && overdue && (
                      <Chip size="small" label="Overdue" sx={{ fontSize: '0.65rem', height: 18, bgcolor: '#fef3c7', color: '#d97706', fontWeight: 700 }} />
                    )}
                    <Chip
                      size="small"
                      label={inv.status.charAt(0).toUpperCase() + inv.status.slice(1)}
                      color={STATUS_COLORS[inv.status] || 'default'}
                      sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                    />
                  </Box>
                </Box>
              );
            })}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button onClick={onClose} sx={{ color: '#6b7280' }}>Done</Button>
        <Button
          variant="contained"
          disabled={!selectedUser}
          onClick={handleSend}
          sx={{
            bgcolor: cfg.color,
            '&:hover': { filter: 'brightness(0.9)', bgcolor: cfg.color },
            textTransform: 'none',
            fontWeight: 600,
          }}
        >
          {actionLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CollaboratorsDialog;
